import { Entity } from './common';
import { Category } from './masters';
import { NativeReturnRequest } from './payment-return';
import { UserGender } from './user';

export interface LicenseRate extends Entity {
  categories: Category[];
  genders: UserGender[];
  price: number;
  licenseType: LicenseType;
  hasSupplements: boolean;
  supplements?: Supplement[];
  publicVisible?: boolean;
}

export interface Supplement {
  for: LicenseType;
  price: number;
}

export enum LicenseType {
  PLAYER = 'PLAYER',
  REFEREE = 'REFEREE',
  DELEGATE = 'DELEGATE',
  INDEPENDENT = 'INDEPENDENT',
  TECHNICIAN = 'TECHNICIAN',
}

export interface LicensePlayerRate {
  category: Category;
  gender: UserGender;
  price: number;
  licenseType: LicenseType;
}

export interface LicenseUserRate extends Entity {
  tenantId: string;
  userId: string;
  category: Category;
  clubId: string;
  gender: UserGender;
  price: number;
  licenseType: LicenseType;
  seasonYear: string; // Year
}

export interface LicenseRateSeason extends Entity {
  teamRates?: Rate;
  licenseRates?: LicenseRate[];
}

export interface Rate {
  standard: number;
  school: number;
  freeTeamsFrom: number;
}

export interface LicensePayment {
  licenseType: LicenseType;
  birthDate?: Date;
  gender?: UserGender;
  price: number;
  userId?: string;
  user?: import('./user').User;
  /** App nativa (#730): rutas de vuelta por deep link tras la pasarela. */
  nativeReturn?: NativeReturnRequest;
}

/**
 * Petición de cotización del importe DEFINITIVO de una licencia
 * (callable `quoteLicensePrice`).
 *
 * `tenantId` viaja en el payload porque el callable es público (el flujo de
 * `/federate` cotiza también sin sesión). Cuando la llamada SÍ está
 * autenticada, el servidor toma el `tenantId` de los claims e ignora este
 * campo (R34).
 */
export interface LicensePriceQuoteRequest {
  tenantId: string;
  licenseType: LicenseType;
  birthDate: string | Date;
  gender: UserGender;
}

/** Motivo por el que no hay importe cotizable. */
export type LicensePriceQuoteReason = 'NO_RATE_FOR_CATEGORY';

/**
 * Cotización del importe definitivo de una licencia. La resuelve el servidor
 * con la MISMA función que usa `payLicense` para cobrar, de modo que el importe
 * mostrado y el cargado no puedan divergir.
 *
 * «Sin tarifa aplicable» NO es un error: se devuelve `available: false` con un
 * `reason`, para que el cliente lo distinga de un fallo técnico.
 */
export interface LicensePriceQuote {
  available: boolean;
  /** Importe definitivo en euros. Solo si `available === true`. */
  price?: number;
  /** Categoría resuelta en servidor (para el mensaje al usuario). */
  category?: Category;
  seasonYear?: string;
  reason?: LicensePriceQuoteReason;
}

export interface LicenseRateRequest {
  licenseType: LicenseType;
}

export interface PublicLicenseRatesRequest {
  tenantId: string;
}
